import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home as HomeIcon, Building2, UtensilsCrossed, Check, ArrowRight, ArrowLeft,
  Loader2, CalendarCheck, PartyPopper, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const SERVICES = {
  residential: [
    { id: "Standard Clean", desc: "Regular upkeep · $150–280" },
    { id: "Deep Clean", desc: "Top-to-bottom detail · $300–450" },
    { id: "Move-In / Move-Out", desc: "Empty-home reset · $250–450" },
  ],
  commercial: [
    { id: "Small Office", desc: "Under 1,000 sq ft · from $250/mo" },
    { id: "Medium Office", desc: "1,000–5,000 sq ft · $500–2,000/mo" },
    { id: "Restaurant / Kitchen", desc: "Custom quote after walkthrough" },
  ],
};

const SIZES = {
  residential: ["Studio / 1 Bedroom", "2 Bedroom", "3 Bedroom", "4+ Bedroom / House"],
  commercial: ["Under 1,000 sq ft", "1,000–2,500 sq ft", "2,500–5,000 sq ft", "5,000+ sq ft"],
};

const TIME_SLOTS = {
  daytime: ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"],
  evening: ["5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM"],
  overnight: ["10:00 PM", "11:00 PM", "12:00 AM", "1:00 AM", "2:00 AM", "3:00 AM", "4:00 AM", "5:00 AM"],
};
const FREQ = ["One-time", "Weekly", "Bi-weekly (save 10-15%)", "Monthly (save 10-15%)"];

const steps = ["Service", "Size", "Schedule", "Details", "Confirm"];

export default function Book() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [d, setD] = useState({
    property_type: "residential", service_type: "", size: "", date: "", time: "",
    frequency: "One-time", name: "", email: "", phone: "", address: "", notes: "",
  });

  const upd = (k, v) => setD({ ...d, [k]: v });
  const today = new Date().toISOString().split("T")[0];

  const canNext = () => {
    if (step === 0) return !!d.service_type;
    if (step === 1) return !!d.size;
    if (step === 2) return !!d.date && !!d.time;
    if (step === 3) return d.name && d.email && d.phone && d.address;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/bookings", { ...d, frequency: d.frequency });
      setDone(true);
      toast.success("Booking confirmed! Check your email.");
    } catch {
      toast.error("Could not submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-sm border border-border bg-white px-4 py-3 text-ink outline-none transition-shadow focus:ring-2 focus:ring-tide focus:border-tide";

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16 bg-secondary grain">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          data-testid="booking-success" className="max-w-lg w-full text-center rounded-sm border border-border bg-white p-10">
          <div className="mx-auto h-16 w-16 rounded-full bg-tide/10 text-tide flex items-center justify-center"><PartyPopper size={30} /></div>
          <h1 className="mt-6 font-heading font-extrabold text-3xl text-ink">You're booked!</h1>
          <p className="mt-3 text-ink/60 leading-relaxed">
            Thanks {d.name.split(" ")[0]} — a confirmation is on its way to <strong className="text-ink">{d.email}</strong>.
            Our team will reach out to confirm details. Final price is set after a free walkthrough.
          </p>
          <div className="mt-6 rounded-sm bg-secondary border border-border p-5 text-left text-sm space-y-2">
            <div className="flex justify-between"><span className="text-ink/50">Service</span><span className="font-semibold text-ink">{d.service_type}</span></div>
            <div className="flex justify-between"><span className="text-ink/50">When</span><span className="font-semibold text-ink">{d.date} · {d.time}</span></div>
            <div className="flex justify-between"><span className="text-ink/50">Frequency</span><span className="font-semibold text-ink">{d.frequency}</span></div>
          </div>
          <a href="/" className="mt-7 inline-flex items-center justify-center gap-2 rounded-sm bg-ink text-white font-semibold px-6 py-3.5 hover:bg-brand transition-colors">Back to Home</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 md:pt-40 pb-20 bg-secondary grain">
      <div className="relative max-w-3xl mx-auto px-5 md:px-8">
        <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-tide">Book Now</span>
        <h1 className="mt-4 font-heading font-extrabold tracking-tighter text-4xl md:text-5xl text-ink">Book your cleaning in minutes.</h1>

        {/* Progress */}
        <div className="mt-10 flex items-center gap-2">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-tide" : "bg-border"}`} />
              <span className={`mt-2 block text-xs font-medium ${i <= step ? "text-tide" : "text-ink/40"}`}>{s}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-sm border border-border bg-white p-7 md:p-9 min-h-[340px]">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
              {step === 0 && (
                <div>
                  <h2 className="font-heading font-bold text-2xl text-ink">What do you need cleaned?</h2>
                  <div className="mt-5 inline-flex rounded-sm border border-border p-1">
                    {[["residential", HomeIcon, "Residential"], ["commercial", Building2, "Commercial"]].map(([k, Icon, l]) => (
                      <button key={k} data-testid={`prop-${k}`} onClick={() => setD({ ...d, property_type: k, service_type: "", size: "", time: "" })}
                        className={`inline-flex items-center gap-2 rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors ${d.property_type === k ? "bg-ink text-white" : "text-ink/60"}`}>
                        <Icon size={16} /> {l}
                      </button>
                    ))}
                  </div>
                  <div className="mt-6 space-y-3">
                    {SERVICES[d.property_type].map((s) => (
                      <button key={s.id} data-testid={`service-${s.id.replace(/[^a-zA-Z]+/g, "-")}`} onClick={() => upd("service_type", s.id)}
                        className={`w-full text-left rounded-sm border p-4 flex items-center justify-between transition-colors ${d.service_type === s.id ? "border-tide ring-1 ring-tide bg-tide/5" : "border-border hover:border-tide/50"}`}>
                        <div>
                          <div className="font-heading font-semibold text-ink flex items-center gap-2">
                            {s.id.includes("Restaurant") && <UtensilsCrossed size={16} className="text-tide" />} {s.id}
                          </div>
                          <div className="text-sm text-ink/55">{s.desc}</div>
                        </div>
                        {d.service_type === s.id && <Check className="text-tide" size={20} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <h2 className="font-heading font-bold text-2xl text-ink">{d.property_type === "residential" ? "How big is your home?" : "How large is the space?"}</h2>
                  <p className="mt-1 text-ink/55">This helps us give an accurate quote.</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {SIZES[d.property_type].map((s) => (
                      <button key={s} data-testid={`size-${s.replace(/[^a-zA-Z0-9]+/g, "-")}`} onClick={() => upd("size", s)}
                        className={`rounded-sm border p-4 text-left font-medium transition-colors ${d.size === s ? "border-tide ring-1 ring-tide bg-tide/5 text-ink" : "border-border text-ink/70 hover:border-tide/50"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="font-heading font-bold text-2xl text-ink">Pick a date &amp; time</h2>
                  <div className="mt-6">
                    <label className="text-sm font-medium text-ink/70">Preferred date</label>
                    <input data-testid="booking-date" type="date" min={today} value={d.date} onChange={(e) => upd("date", e.target.value)} className={inputCls + " mt-1.5"} />
                  </div>
                  <div className="mt-5">
                    <label className="text-sm font-medium text-ink/70">Preferred time</label>

                    <div className="mt-3">
                      <div className="text-xs font-bold uppercase tracking-wide text-tide">Daytime · 9am–5pm</div>
                      <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                        {TIME_SLOTS.daytime.map((t) => (
                          <button key={t} data-testid={`time-${t.replace(/[^a-zA-Z0-9]+/g, "-")}`} onClick={() => upd("time", t)}
                            className={`rounded-sm border py-2.5 text-sm font-medium transition-colors ${d.time === t ? "border-tide ring-1 ring-tide bg-tide/5 text-ink" : "border-border text-ink/70 hover:border-tide/50"}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    {d.property_type === "commercial" && (
                      <>
                        <div className="mt-5">
                          <div className="text-xs font-bold uppercase tracking-wide text-tide">Evening · 5pm–10pm</div>
                          <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                            {TIME_SLOTS.evening.map((t) => (
                              <button key={t} data-testid={`time-${t.replace(/[^a-zA-Z0-9]+/g, "-")}`} onClick={() => upd("time", t)}
                                className={`rounded-sm border py-2.5 text-sm font-medium transition-colors ${d.time === t ? "border-tide ring-1 ring-tide bg-tide/5 text-ink" : "border-border text-ink/70 hover:border-tide/50"}`}>
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="mt-5">
                          <div className="text-xs font-bold uppercase tracking-wide text-tide">Overnight · 10pm–6am</div>
                          <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                            {TIME_SLOTS.overnight.map((t) => (
                              <button key={t} data-testid={`time-${t.replace(/[^a-zA-Z0-9]+/g, "-")}`} onClick={() => upd("time", t)}
                                className={`rounded-sm border py-2.5 text-sm font-medium transition-colors ${d.time === t ? "border-tide ring-1 ring-tide bg-tide/5 text-ink" : "border-border text-ink/70 hover:border-tide/50"}`}>
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div data-testid="afterhours-note" className="mt-4 flex items-start gap-2 rounded-sm border border-tide/30 bg-tide/5 p-3 text-sm text-ink/70">
                          <Clock size={16} className="text-tide mt-0.5 shrink-0" />
                          <span>Evening &amp; overnight slots are for businesses that need cleaning outside operating hours — so we're done before your team arrives.</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-5">
                    <label className="text-sm font-medium text-ink/70">Frequency</label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-3">
                      {FREQ.map((f) => (
                        <button key={f} onClick={() => upd("frequency", f)}
                          className={`rounded-sm border py-2.5 px-3 text-sm font-medium text-left transition-colors ${d.frequency === f ? "border-tide ring-1 ring-tide bg-tide/5 text-ink" : "border-border text-ink/70 hover:border-tide/50"}`}>
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="font-heading font-bold text-2xl text-ink">Your contact details</h2>
                  <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <div><label className="text-sm font-medium text-ink/70">Full name *</label><input data-testid="booking-name" className={inputCls + " mt-1.5"} value={d.name} onChange={(e) => upd("name", e.target.value)} placeholder="Jane Doe" /></div>
                    <div><label className="text-sm font-medium text-ink/70">Phone *</label><input data-testid="booking-phone" className={inputCls + " mt-1.5"} value={d.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="(416) 000-0000" /></div>
                  </div>
                  <div className="mt-5"><label className="text-sm font-medium text-ink/70">Email *</label><input data-testid="booking-email" type="email" className={inputCls + " mt-1.5"} value={d.email} onChange={(e) => upd("email", e.target.value)} placeholder="you@email.com" /></div>
                  <div className="mt-5"><label className="text-sm font-medium text-ink/70">Service address *</label><input data-testid="booking-address" className={inputCls + " mt-1.5"} value={d.address} onChange={(e) => upd("address", e.target.value)} placeholder="Street, City, GTA" /></div>
                  <div className="mt-5"><label className="text-sm font-medium text-ink/70">Notes (optional)</label><textarea data-testid="booking-notes" rows={3} className={inputCls + " mt-1.5 resize-none"} value={d.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Anything we should know?" /></div>
                </div>
              )}

              {step === 4 && (
                <div>
                  <h2 className="font-heading font-bold text-2xl text-ink flex items-center gap-2"><CalendarCheck className="text-tide" /> Review &amp; confirm</h2>
                  <div className="mt-6 divide-y divide-border rounded-sm border border-border">
                    {[
                      ["Service", `${d.service_type} (${d.property_type})`],
                      ["Size", d.size],
                      ["Date & Time", `${d.date} · ${d.time}`],
                      ["Frequency", d.frequency],
                      ["Name", d.name],
                      ["Email", d.email],
                      ["Phone", d.phone],
                      ["Address", d.address],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between px-4 py-3 text-sm">
                        <span className="text-ink/50">{k}</span><span className="font-semibold text-ink text-right">{v}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-ink/55 italic">Final price confirmed after a free walkthrough or quote. No payment required now.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Nav buttons */}
        <div className="mt-6 flex justify-between">
          <button data-testid="booking-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="inline-flex items-center gap-2 rounded-sm border border-ink/20 text-ink font-semibold px-6 py-3 disabled:opacity-40 hover:border-tide transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
          {step < 4 ? (
            <button data-testid="booking-next" onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete this step.")}
              className="inline-flex items-center gap-2 rounded-sm bg-ink text-white font-semibold px-7 py-3 hover:bg-brand transition-colors">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="booking-confirm" onClick={submit} disabled={loading}
              className="inline-flex items-center gap-2 rounded-sm bg-tide text-white font-semibold px-8 py-3 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Confirm Booking
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
